import {
  type SocialPlatformId,
  getSocialPlatformLabel,
  inferSocialPlatformFromLabel,
  inferSocialPlatformFromUrl,
  isSocialPlatformId,
  urlMatchesSocialPlatform,
} from '@/lib/social-platforms'
import { normalizeHexColor, validatePrimaryColor } from '@/lib/tenant-theme-colors'

import { NON_FSDS_DEFAULT_LOGO_URL } from '@/configs/non-fsds-config'
import {
  ClearableTenantMediaDefaults,
  TenantCustomization,
  UpdateMyMediaDefaultsPayload,
} from '@/services/tenant.service'

export const GLOBAL_DEFAULT_AVATAR_URL = '/images/avatar.png'
export const GLOBAL_DEFAULT_THUMBNAIL_URL = '/images/og.jpg'

export interface MediaDefaultSocialLink {
  platform: SocialPlatformId
  url: string
}

/** Email templates and legacy APIs still expect label + url. */
export interface MediaDefaultSocialLinkWithLabel {
  label?: string
  url: string
  platform?: SocialPlatformId
}

export interface TenantMediaDefaults {
  defaultAvatarUrl?: string | null
  defaultThumbnailUrl?: string | null
  defaultFaviconUrl?: string | null
  defaultLogoUrl?: string | null
  socialLinks?: MediaDefaultSocialLink[] | null
  senderName?: string | null
  senderEmail?: string | null
  primaryColor?: string | null
}

export interface MediaDefaultsFormValues {
  defaultAvatarUrl: string
  defaultThumbnailUrl: string
  defaultFaviconUrl: string
  defaultLogoUrl: string
  socialLinks: MediaDefaultSocialLink[]
  senderName: string
  senderEmail: string
  primaryColor: string
}

export interface MediaDefaultsOptions {
  /** When false, FSDS global fallbacks (avatar.png / og.jpg) are never used. */
  isFsdsDomain?: boolean
}

function normalizeStoredSocialLink(raw: MediaDefaultSocialLinkWithLabel): MediaDefaultSocialLink | null {
  const url = raw.url?.trim()
  if (!url) {
    return null
  }

  const platform =
    (raw.platform && isSocialPlatformId(raw.platform) ? raw.platform : null) ??
    inferSocialPlatformFromUrl(url) ??
    (raw.label ? inferSocialPlatformFromLabel(raw.label) : null)

  if (!platform || !urlMatchesSocialPlatform(url, platform)) {
    return null
  }

  return {
    platform,
    url,
  }
}

export function resolveMediaDefaultSocialLinks(
  customization?: TenantCustomization
): MediaDefaultSocialLink[] {
  const { socialLinks } = getTenantMediaDefaults(customization)
  const normalized =
    socialLinks
      ?.map(link => normalizeStoredSocialLink(link as MediaDefaultSocialLinkWithLabel))
      .filter((link): link is MediaDefaultSocialLink => Boolean(link)) ?? []

  const seen = new Set<SocialPlatformId>()
  return normalized.filter(link => {
    if (seen.has(link.platform)) {
      return false
    }
    seen.add(link.platform)
    return true
  })
}

export function toEmailSocialLinks(
  links: MediaDefaultSocialLink[]
): { label: string; url: string }[] {
  return links.map(link => ({
    label: getSocialPlatformLabel(link.platform),
    url: link.url,
  }))
}

export function getTenantMediaDefaults(customization?: TenantCustomization): TenantMediaDefaults {
  const mediaDefaults = customization?.mediaDefaults as TenantMediaDefaults | undefined
  return {
    defaultAvatarUrl: mediaDefaults?.defaultAvatarUrl ?? null,
    defaultThumbnailUrl: mediaDefaults?.defaultThumbnailUrl ?? null,
    defaultFaviconUrl: mediaDefaults?.defaultFaviconUrl ?? null,
    defaultLogoUrl: mediaDefaults?.defaultLogoUrl ?? null,
    socialLinks: mediaDefaults?.socialLinks ?? null,
    senderName: mediaDefaults?.senderName ?? null,
    senderEmail: mediaDefaults?.senderEmail ?? null,
    primaryColor: mediaDefaults?.primaryColor ?? null,
  }
}

/** Resolved primary brand color for community sites (media defaults, then theme). */
export function resolveTenantPrimaryColor(
  customization?: TenantCustomization,
  themePrimaryColor?: string | null
): string | undefined {
  const { primaryColor } = getTenantMediaDefaults(customization)
  const fromMedia = primaryColor?.trim()
  if (fromMedia) {
    return normalizeHexColor(fromMedia) ?? fromMedia
  }
  const fromTheme = themePrimaryColor?.trim()
  if (fromTheme) {
    return normalizeHexColor(fromTheme) ?? fromTheme
  }
  return undefined
}

/** Community-wide logo for header, footer, and pages without a Puck custom logo. */
export function resolveDefaultLogoUrl(
  customization?: TenantCustomization,
  themeLogo?: string | null
): string | undefined {
  const { defaultLogoUrl } = getTenantMediaDefaults(customization)
  const fromMedia = defaultLogoUrl?.trim()
  if (fromMedia) {
    return fromMedia
  }
  const fromTheme = themeLogo?.trim()
  return fromTheme || NON_FSDS_DEFAULT_LOGO_URL
}

export function resolveDefaultAvatarUrl(
  customization?: TenantCustomization,
  options?: MediaDefaultsOptions
): string | undefined {
  const { defaultAvatarUrl } = getTenantMediaDefaults(customization)
  if (defaultAvatarUrl) {
    return defaultAvatarUrl
  }
  if (options?.isFsdsDomain === false) {
    return undefined
  }
  return GLOBAL_DEFAULT_AVATAR_URL
}

export function resolveDefaultThumbnailUrl(
  customization?: TenantCustomization,
  options?: MediaDefaultsOptions
): string | undefined {
  const { defaultThumbnailUrl } = getTenantMediaDefaults(customization)
  if (defaultThumbnailUrl) {
    return defaultThumbnailUrl
  }
  if (options?.isFsdsDomain === false) {
    return undefined
  }
  return GLOBAL_DEFAULT_THUMBNAIL_URL
}

export function resolveDefaultFaviconUrl(customization?: TenantCustomization): string | undefined {
  const { defaultFaviconUrl } = getTenantMediaDefaults(customization)
  const trimmed = defaultFaviconUrl?.trim()
  return trimmed || undefined
}

/** Community consultation CTA: Facebook, then LinkedIn, then first configured link. */
export function resolveConsultationUrl(customization?: TenantCustomization): string | undefined {
  const links = resolveMediaDefaultSocialLinks(customization)
  const facebook = links.find(link => link.platform === 'facebook')?.url
  if (facebook) {
    return facebook
  }
  const linkedin = links.find(link => link.platform === 'linkedin')?.url
  if (linkedin) {
    return linkedin
  }
  return links[0]?.url
}

/** True when no custom avatar is set (empty or global FSDS placeholder). */
export function isUnsetAvatarUrl(avatar: string | undefined | null): boolean {
  const trimmed = avatar?.trim()
  return !trimmed || trimmed === GLOBAL_DEFAULT_AVATAR_URL
}

export function resolveAvatarUrl(
  avatar: string | undefined | null,
  customization?: TenantCustomization,
  options?: MediaDefaultsOptions
): string | undefined {
  const trimmed = avatar?.trim()
  if (trimmed && !isUnsetAvatarUrl(avatar)) {
    return trimmed
  }
  return resolveDefaultAvatarUrl(customization, options)
}

export function resolveThumbnailUrl(
  thumbnail: string | undefined | null,
  customization?: TenantCustomization,
  options?: MediaDefaultsOptions
): string | undefined {
  if (thumbnail?.trim()) {
    return thumbnail
  }
  return resolveDefaultThumbnailUrl(customization, options)
}

export function getMediaDefaultsFormValues(
  customization?: TenantCustomization,
  themeLogo?: string | null,
  themePrimaryColor?: string | null
): MediaDefaultsFormValues {
  const mediaDefaults = getTenantMediaDefaults(customization)
  const resolvedPrimary =
    resolveTenantPrimaryColor(customization, themePrimaryColor) || ''
  return {
    defaultAvatarUrl: mediaDefaults.defaultAvatarUrl || '',
    defaultThumbnailUrl: mediaDefaults.defaultThumbnailUrl || '',
    defaultFaviconUrl: mediaDefaults.defaultFaviconUrl || '',
    defaultLogoUrl: mediaDefaults.defaultLogoUrl || themeLogo?.trim() || '',
    socialLinks: resolveMediaDefaultSocialLinks(customization),
    senderName: mediaDefaults.senderName || '',
    senderEmail: mediaDefaults.senderEmail || '',
    primaryColor: resolvedPrimary,
  }
}

const cleanString = (value: string | undefined) => value?.trim() || null

export function normalizeMediaDefaultsPayload(values: MediaDefaultsFormValues): UpdateMyMediaDefaultsPayload {
  const socialLinks = values.socialLinks
    .map(link => ({
      platform: link.platform,
      url: link.url.trim(),
    }))
    .filter(link => link.url && urlMatchesSocialPlatform(link.url, link.platform))

  return {
    mediaDefaults: {
      defaultAvatarUrl: cleanString(values.defaultAvatarUrl),
      defaultThumbnailUrl: cleanString(values.defaultThumbnailUrl),
      defaultFaviconUrl: cleanString(values.defaultFaviconUrl),
      defaultLogoUrl: cleanString(values.defaultLogoUrl),
      socialLinks: socialLinks.length ? socialLinks : null,
      senderName: cleanString(values.senderName),
      senderEmail: cleanString(values.senderEmail),
      primaryColor: normalizeHexColor(values.primaryColor),
    },
  }
}

export { validatePrimaryColor }

export type { ClearableTenantMediaDefaults }
